import { useOrder } from "@utils/store/cart/useOrder";
import { formatNextOpen, getReservableDays, isSlotPast, isSlotSelected, slotAvailable, todayStr } from "@utils/func/deliverySlots";
import RestNoticeDialog from "@components/delivery/RestNoticeDialog";
import { useEffect, useMemo, useState } from "react";
import { ScrollView, Text, XStack, YStack } from "tamagui";
import KioskIcon from "../KioskIcon";
import { kiosk } from "../kioskTheme";

const WD = ["日", "一", "二", "三", "四", "五", "六"];
// 停用（非營業時的「立即配送」）樣式，依核准設計稿
const OFF_BG = "#f1f3f6";
const OFF_INK = "#acacac";

/** 日期字串 → 星期幾 */
function weekday(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return WD[new Date(y, mo - 1, d).getDay()];
}

/**
 * Kiosk 專屬配送時間選擇器（取代 GasShop 的 DeliverTimeCard）：
 * 橫向日期條 + 清楚的時段列，符合點餐機風格、文字顏色明確可見。
 * 共用 useOrder 的 deliverySlots / deliveryTime，CheckoutGuide 的驗證邏輯不變。
 */
const KioskTimePicker = () => {
  const { deliverySlots, deliveryTime, setDeliveryTime, isOpenNow, allowReservation, nextOpenAt } = useOrder();
  // 非營業時點「立即配送」→ 休息中提示（只有「確定」）
  const [restNotice, setRestNotice] = useState<string | null>(null);

  // 可選日期：API 有回、非整日公休、且至少一個可用時段（與入口閘門共用同一判斷）
  const days = useMemo(() => getReservableDays(deliverySlots), [deliverySlots]);

  // 目前可用的配送方式：立即僅營業中(isOpenNow)、預約僅瓦斯行開放預約(allowReservation)。
  const canScheduled = allowReservation;
  const canImmediate = isOpenNow;

  // 有效配送方式（correct-by-construction）：store 內選取的 type 若目前不可用，直接落到另一種可用方式來渲染，
  // 確保未開放預約時「絕不」顯示日期/時段區（不依賴 effect 是否已 commit）。
  const effectiveType: "scheduled" | "immediate" =
    deliveryTime.type === "scheduled" && !canScheduled
      ? "immediate"
      : deliveryTime.type === "immediate" && !canImmediate
        ? "scheduled"
        : deliveryTime.type;

  // 把修正後的 type 同步回 store（供 CheckoutGuide 驗證與後續結帳一致）
  useEffect(() => {
    if (deliveryTime.type !== effectiveType) {
      setDeliveryTime({ ...deliveryTime, type: effectiveType, date: null, slotId: null, slotSource: null, slotLabel: null });
    }
  }, [effectiveType]);

  // 預約模式下校正日期與時段（correct-by-construction）：
  // 1. 尚未選日期、或所選日期已不在可預約清單中（跨日殘留、整日公休、當日時段全過）→ 落到第一個可用日
  // 2. 所選時段已不在今次清單中或已過時／額滿 → 清掉
  // 不校正的話，草稿帶回或 sessionStorage 殘留的舊 date/slotId 會變成「畫面上沒有任何東西被選取，
  // 但 CheckoutGuide 的『下一步』卻可以按」——使用者沒選時段就進到下一步。
  useEffect(() => {
    if (effectiveType !== "scheduled" || !canScheduled) return;
    const dateOk = !!deliveryTime.date && days.includes(deliveryTime.date);
    if (!dateOk) {
      const fallback = days.length > 0 ? days[0] : null;
      if (deliveryTime.date !== fallback || deliveryTime.slotId !== null) {
        setDeliveryTime({ ...deliveryTime, date: fallback, slotId: null, slotSource: null, slotLabel: null });
      }
      return;
    }
    if (deliveryTime.slotId !== null && !isSlotSelected(deliverySlots, deliveryTime.date, deliveryTime.slotId)) {
      setDeliveryTime({ ...deliveryTime, slotId: null, slotSource: null, slotLabel: null });
    }
  }, [days, deliverySlots, effectiveType, canScheduled, deliveryTime.date, deliveryTime.slotId]);

  const selectedDay = (deliverySlots || []).find((d) => d.delivery_date === deliveryTime.date) || null;

  // 配送方式選項：預約僅在瓦斯行開放預約時出現；立即配送一律顯示，非營業時為停用樣式（點了跳休息中提示）。
  const deliveryOptions = [
    ...(canScheduled ? [{ t: "scheduled" as const, label: "預約配送", sub: "指定日期與時段", off: false }] : []),
    { t: "immediate" as const, label: "立即配送", sub: "將儘速為您配送", off: !canImmediate },
  ];

  const setType = (type: "scheduled" | "immediate") =>
    setDeliveryTime({ ...deliveryTime, type, date: null, slotId: null, slotSource: null, slotLabel: null });
  const pickDate = (date: string) => setDeliveryTime({ ...deliveryTime, date, slotId: null, slotSource: null, slotLabel: null });
  const pickSlot = (s: any) =>
    setDeliveryTime({ ...deliveryTime, slotId: s.slot_id, slotSource: s.slot_source, slotLabel: `${s.start_time} - ${s.end_time}` });

  return (
    <YStack gap={16}>
      {/* 配送方式 */}
      <XStack gap={11}>
        {deliveryOptions.map((o) => {
          const sel = !o.off && effectiveType === o.t;
          return (
            <YStack
              key={o.t}
              flex={1}
              ai="center"
              py={16}
              borderRadius={16}
              borderWidth={2}
              borderColor={sel ? kiosk.blue : kiosk.line}
              backgroundColor={o.off ? OFF_BG : sel ? kiosk.pillBg : "#fff"}
              cursor="pointer"
              role="button"
              testID={o.off ? `delivery-option-${o.t}-off` : `delivery-option-${o.t}`}
              onPress={() => (o.off ? setRestNotice(formatNextOpen(nextOpenAt)) : setType(o.t))}
              pressStyle={{ scale: 0.97 }}>
              <Text fontSize={15} fontWeight="800" color={o.off ? OFF_INK : sel ? kiosk.blue : "#33405c"}>
                {o.label}
              </Text>
              <Text fontSize={11} color={o.off ? OFF_INK : kiosk.grey} mt={3}>
                {o.sub}
              </Text>
            </YStack>
          );
        })}
      </XStack>
      <RestNoticeDialog open={restNotice !== null} message={restNotice ?? ""} onClose={() => setRestNotice(null)} />

      {effectiveType === "immediate" ? (
        <YStack backgroundColor="#f4f6fb" borderRadius={14} p={16} ai="center" gap={6}>
          <KioskIcon name="clock" size={34} color={kiosk.blue} />
          <Text fontSize={14} fontWeight="700" color={kiosk.ink}>
            下單後將儘速為您安排配送人員
          </Text>
          <Text fontSize={12} color={kiosk.grey} textAlign="center">
            有任何問題可以聯絡瓦斯行
          </Text>
        </YStack>
      ) : (
        <>
          {/* 目前可選範圍的年月（跨月時依所選日期更新） */}
          {(() => {
            const ref = deliveryTime.date || days[0] || "";
            if (!ref) return null;
            return (
              <Text fontSize={13} fontWeight="700" color={kiosk.ink}>
                {ref.slice(0, 4)} 年 {Number(ref.slice(5, 7))} 月
              </Text>
            );
          })()}
          {/* 橫向日期條 */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} className="kiosk-noscroll">
            <XStack gap={8} pb={2}>
              {days.map((d) => {
                const sel = deliveryTime.date === d;
                const isToday = d === todayStr();
                const dayNum = Number(d.split("-")[2]);
                return (
                  <YStack
                    key={d}
                    width={52}
                    py={9}
                    ai="center"
                    borderRadius={13}
                    borderWidth={2}
                    borderColor={sel ? kiosk.blue : kiosk.line}
                    backgroundColor={sel ? kiosk.blue : "#fff"}
                    cursor="pointer"
                    onPress={() => pickDate(d)}
                    pressStyle={{ scale: 0.93 }}>
                    <Text fontSize={11} color={sel ? "#fff" : kiosk.grey}>
                      {isToday ? "今天" : weekday(d)}
                    </Text>
                    <Text fontSize={18} fontWeight="800" mt={2} color={sel ? "#fff" : "#33405c"}>
                      {dayNum}
                    </Text>
                  </YStack>
                );
              })}
              {days.length === 0 && (
                <Text fontSize={13} color={kiosk.grey} py={10}>
                  目前無可預約日期
                </Text>
              )}
            </XStack>
          </ScrollView>

          {/* 時段 */}
          {deliveryTime.date && (
            <YStack gap={9}>
              {(selectedDay?.slots || []).map((s) => {
                const avail = slotAvailable(deliveryTime.date!, s);
                const sel = deliveryTime.slotId === s.slot_id;
                return (
                  <XStack
                    key={s.slot_id}
                    ai="center"
                    jc="space-between"
                    px={14}
                    py={14}
                    borderRadius={14}
                    borderWidth={2}
                    borderColor={sel ? kiosk.blue : kiosk.line}
                    backgroundColor={sel ? kiosk.pillBg : "#fff"}
                    opacity={avail ? 1 : 0.45}
                    cursor={avail ? "pointer" : "not-allowed"}
                    onPress={avail ? () => pickSlot(s) : undefined}
                    pressStyle={avail ? { scale: 0.98 } : undefined}>
                    <XStack ai="center" gap={9}>
                      <KioskIcon name="clock" size={18} color={sel ? kiosk.blue : kiosk.grey} />
                      <Text fontSize={15} fontWeight={sel ? "800" : "700"} color={sel ? kiosk.blue : "#33405c"}>
                        {s.start_time} - {s.end_time}
                      </Text>
                    </XStack>
                    <XStack ai="center" gap={8}>
                      <Text fontSize={12} color={avail ? kiosk.grey : kiosk.danger}>
                        {avail ? (s.available_capacity !== undefined ? `剩餘 ${s.available_capacity}` : "可預約") : isSlotPast(deliveryTime.date!, s.end_time) ? "已過時段" : "已額滿"}
                      </Text>
                      {sel && <KioskIcon name="check" size={16} color={kiosk.blue} strokeWidth={2.6} />}
                    </XStack>
                  </XStack>
                );
              })}
              {(!selectedDay?.slots || selectedDay.slots.length === 0) && (
                <Text fontSize={13} color={kiosk.grey} textAlign="center" py={6}>
                  當日無可選時段
                </Text>
              )}
            </YStack>
          )}
        </>
      )}
    </YStack>
  );
};

export default KioskTimePicker;
